import { hostStore } from './address.js';
import {
  type StoreGroupKind,
  type StoreProduct,
  type Storefront,
  storeGroupPath,
  storeProductPath,
  storefrontSchema,
} from '@arabat/contracts';
import { isPreview, leaveRefusedPreview } from './design/context.js';
import { ApiError } from '@arabat/api-client';
import { useApi } from '@arabat/web-core';
import { useQuery } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/** The pickup branch the shopper chose (ADR-0123), kept per store in the browser. */
const BranchContext = createContext<{
  branchId: string | null;
  setBranchId: (id: string | null) => void;
} | null>(null);

const branchStorageKey = (slug: string) => `arabat.store.${slug}.branch`;

export function BranchChoiceProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [branchId, setState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(branchStorageKey(slug));
    } catch {
      return null;
    }
  });
  const setBranchId = useCallback(
    (id: string | null) => {
      setState(id);
      try {
        if (id) localStorage.setItem(branchStorageKey(slug), id);
        else localStorage.removeItem(branchStorageKey(slug));
      } catch {
        // Private mode: the choice lives for this page only.
      }
    },
    [slug],
  );
  const value = useMemo(() => ({ branchId, setBranchId }), [branchId, setBranchId]);
  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
}

/**
 * How the shopper is served (ADR-0141), kept per store in the browser: delivery to an area
 * (asked on entering when the store wants it, or picked at checkout) or pickup. `dismissed`
 * means "browse without choosing" for this visit.
 */
export type DeliveryChoice =
  { mode: 'delivery'; areaId: string; placeId: string | null } | { mode: 'pickup' };
const DeliveryContext = createContext<{
  choice: DeliveryChoice | null;
  setChoice: (choice: DeliveryChoice | null) => void;
  dismissed: boolean;
  dismiss: () => void;
} | null>(null);
const choiceStorageKey = (slug: string) => `arabat.store.${slug}.serve`;

export function DeliveryChoiceProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [choice, setState] = useState<DeliveryChoice | null>(() => {
    try {
      const raw = localStorage.getItem(choiceStorageKey(slug));
      return raw ? (JSON.parse(raw) as DeliveryChoice) : null;
    } catch {
      return null;
    }
  });
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(choiceStorageKey(slug)) === 'browse';
    } catch {
      return false;
    }
  });
  const setChoice = useCallback(
    (next: DeliveryChoice | null) => {
      setState(next);
      try {
        if (next) localStorage.setItem(choiceStorageKey(slug), JSON.stringify(next));
        else localStorage.removeItem(choiceStorageKey(slug));
      } catch {
        // Private mode: the choice lives for this page only.
      }
    },
    [slug],
  );
  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(choiceStorageKey(slug), 'browse');
    } catch {
      // Private mode: asked again on the next page load.
    }
  }, [slug]);
  const value = useMemo(
    () => ({ choice, setChoice, dismissed, dismiss }),
    [choice, setChoice, dismissed, dismiss],
  );
  return <DeliveryContext.Provider value={value}>{children}</DeliveryContext.Provider>;
}

/** The shopper's delivery area or pickup choice, when made (ADR-0141). */
export function useDeliveryChoice() {
  const ctx = useContext(DeliveryContext);
  return {
    choice: ctx?.choice ?? null,
    setChoice: ctx?.setChoice ?? (() => undefined),
    dismissed: ctx?.dismissed ?? false,
    dismiss: ctx?.dismiss ?? (() => undefined),
  };
}

/** The shopper's account token (ADR-0124), kept per store in the browser. */
const AccountContext = createContext<{
  token: string | null;
  setToken: (token: string | null) => void;
} | null>(null);
const tokenStorageKey = (slug: string) => `arabat.store.${slug}.token`;

export function AccountProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [token, setState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(tokenStorageKey(slug));
    } catch {
      return null;
    }
  });
  const setToken = useCallback(
    (next: string | null) => {
      setState(next);
      try {
        if (next) localStorage.setItem(tokenStorageKey(slug), next);
        else localStorage.removeItem(tokenStorageKey(slug));
      } catch {
        // Private mode: signed in for this page only.
      }
    },
    [slug],
  );
  const value = useMemo(() => ({ token, setToken }), [token, setToken]);
  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

/** Token and sign-in state of the shopper; `account` loads when a token is present. */
export function useAccount() {
  const ctx = useContext(AccountContext);
  const api = useApi();
  const slug = useSlug();
  const token = ctx?.token ?? null;
  const setToken = ctx?.setToken ?? (() => undefined);
  const view = useQuery({
    queryKey: ['store', slug, 'account', token],
    queryFn: () => api.store.account.me(slug, token!),
    enabled: token !== null,
    retry: false,
  });
  // A revoked or expired token signs the shopper out quietly.
  if (view.isError && token) setToken(null);
  return {
    token,
    setToken,
    account: view.data?.account ?? null,
    addresses: view.data?.addresses ?? [],
    isPending: token !== null && view.isPending,
    signOut: async () => {
      if (token) await api.store.account.logout(slug, token).catch(() => undefined);
      setToken(null);
    },
  };
}

/**
 * The branch products, stock and prices come from: the shopper's choice when it serves the
 * store, else the store's default branch.
 */
export function useBranchChoice() {
  const ctx = useContext(BranchContext);
  const delivery = useContext(DeliveryContext)?.choice ?? null;
  const { data: store } = useStorefront();
  const chosen = ctx?.branchId ?? null;
  // A chosen delivery area names the branch that would deliver (ADR-0141): its stock shows.
  const areaBranch =
    delivery?.mode === 'delivery'
      ? store?.deliveryAreas.find((a) => a.id === delivery.areaId)?.branchId
      : undefined;
  const branch =
    (chosen ? store?.branches.find((b) => b.id === chosen) : undefined) ??
    (areaBranch ? store?.branches.find((b) => b.id === areaBranch) : undefined) ??
    store?.branches.find((b) => b.id === store.defaultBranchId) ??
    store?.branches[0] ??
    null;
  return {
    branch,
    branchId: branch?.id ?? null,
    isDefault: branch === null || branch.id === store?.defaultBranchId,
    setBranchId: ctx?.setBranchId ?? (() => undefined),
  };
}

export function useSlug(): string {
  const param = useParams()['slug'];
  // On the store's own sub-domain the host names the store (ADR-0136).
  return hostStore() ?? param ?? '';
}

let embedded: { data: Storefront | null; at: number } | undefined;

/**
 * The store's data the server put inside the page (ADR-0159), or null: read once, when the page
 * loads. Office web filters may refuse `/api/v1/store/{slug}` while letting the page through.
 */
export function embeddedStorefront(): { data: Storefront | null; at: number } {
  if (embedded) return embedded;
  let data: Storefront | null = null;
  try {
    const text = document.getElementById('arabat-storefront')?.textContent;
    const parsed = text ? storefrontSchema.safeParse(JSON.parse(text)) : null;
    if (parsed?.success) data = parsed.data;
  } catch {
    data = null;
  }
  embedded = { data, at: Date.now() };
  return embedded;
}

/** For tests: forget the data read from the page. */
export function resetEmbeddedStorefront() {
  embedded = undefined;
}

export function useStorefront() {
  const api = useApi();
  const slug = useSlug();
  // Inside the console's preview frame: the own store, also while it is a draft (ADR-0128).
  const preview = isPreview();
  // The data sent with the page shows the store at once; it is refreshed after a minute, and
  // stays in use when the refresh is refused on the way (a web filter, ADR-0159).
  const sent = preview ? null : embeddedStorefront();
  const initial = sent?.data && sent.data.slug === slug ? sent.data : undefined;
  return useQuery({
    queryKey: ['store', slug, preview],
    ...(initial ? { initialData: initial, initialDataUpdatedAt: sent!.at, staleTime: 60_000 } : {}),
    queryFn: async () => {
      if (!preview) {
        try {
          return await api.store.info(slug);
        } catch (error) {
          if (initial && !(error instanceof ApiError && error.status === 404)) return initial;
          throw error;
        }
      }
      try {
        return await api.online.previewStorefront();
      } catch (error) {
        leaveRefusedPreview((error as { status?: number }).status);
        throw error;
      }
    },
  });
}

/** Catalog with live availability; refreshed often because POS sales change stock. */
export function useStoreProducts() {
  const api = useApi();
  const slug = useSlug();
  const { branchId, isDefault } = useBranchChoice();
  return useQuery({
    queryKey: ['store', slug, 'products', isDefault ? null : branchId, isPreview()],
    queryFn: () =>
      isPreview()
        ? api.online.previewProducts(isDefault ? null : branchId)
        : api.store.products(slug, isDefault ? null : branchId),
    staleTime: 15_000,
  });
}

/** Picks the name in the shopper's language, falling back to the other one. */
export function useLocalName() {
  const { i18n } = useTranslation();
  return (item: { nameEn: string | null; nameAr: string | null }) =>
    (i18n.language.startsWith('ar') ? item.nameAr || item.nameEn : item.nameEn || item.nameAr) ??
    '';
}

/**
 * A product's pictures' alternative text (owner decision 2026-11-03): the merchant's in the
 * shopper's language, else the other language's, else the product's name.
 */
export function useImageAlt() {
  const { i18n } = useTranslation();
  const name = useLocalName();
  return (p: StoreProduct) =>
    (i18n.language.startsWith('ar')
      ? p.imageAltAr || p.imageAltEn
      : p.imageAltEn || p.imageAltAr) || name(p);
}

type PageMeta = Parameters<typeof setPageMeta>[0];
let storeMeta: PageMeta | null = null;
let ownMeta = false;
/**
 * The store's own title and description, kept so a page that set its own can give them back.
 * Not applied while such a page is open (the shell's effect runs after the page's).
 */
export function setStoreMeta(meta: PageMeta) {
  storeMeta = meta;
  if (!ownMeta) setPageMeta(meta);
}
/** A page's own title (a product); `restoreStoreMeta` gives the store's back on leaving. */
export function setOwnPageMeta(meta: PageMeta) {
  ownMeta = true;
  setPageMeta(meta);
}
/** Back to the store's title (a product page leaving: the tab kept the product's name). */
export function restoreStoreMeta() {
  ownMeta = false;
  if (storeMeta) setPageMeta(storeMeta);
}

/** Page metadata for search engines and link previews (Phase 10): title, description, OG tags, JSON-LD. */
export function setPageMeta(meta: {
  title: string;
  description?: string | null;
  image?: string | null;
  jsonLd?: Record<string, unknown> | null;
}) {
  document.title = meta.title;
  const tag = (selector: string, create: () => HTMLElement) => {
    let el = document.head.querySelector<HTMLElement>(selector);
    if (!el) {
      el = create();
      document.head.appendChild(el);
    }
    return el;
  };
  const metaTag = (attr: 'name' | 'property', key: string, content: string | null | undefined) => {
    const el = tag(`meta[${attr}="${key}"]`, () => {
      const m = document.createElement('meta');
      m.setAttribute(attr, key);
      return m;
    });
    if (content) el.setAttribute('content', content);
    else el.remove();
  };
  metaTag('name', 'description', meta.description);
  metaTag('property', 'og:title', meta.title);
  metaTag('property', 'og:description', meta.description);
  metaTag(
    'property',
    'og:image',
    meta.image ? new URL(meta.image, window.location.origin).toString() : null,
  );
  metaTag('property', 'og:type', meta.jsonLd ? 'product' : 'website');
  // The server's structured data (product and breadcrumbs) stays while the shopper is on the
  // page it was made for: one Product per page, never a second one from here.
  const served = document.head.querySelector<HTMLElement>('script[data-seo="jsonld"][data-page]');
  if (served) {
    if (served.dataset['page'] === window.location.pathname + window.location.search) return;
    served.removeAttribute('data-page');
  }
  const ld = tag('script[data-seo="jsonld"]', () => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo', 'jsonld');
    return s;
  });
  if (meta.jsonLd) ld.textContent = JSON.stringify(meta.jsonLd);
  else ld.remove();
}

/**
 * Categories and collections (ADR-0130): a `?category=` address, a link or a section may name
 * either. The store categories of ADR-0121 became collections with the same ids, so designs
 * and links made before keep working.
 */
/** The group id of the "Sale" page: every product with a product discount in force (ADR-0146). */
export const SALE_GROUP = 'sale';
export const onSale = (p: StoreProduct) => p.variants.some((v) => v.regularPrice !== null);
/**
 * Brands (ADR-0146) are groups too, and a product is in its category's parents: a link to a
 * parent category, a brand or the sale page filters the same product list.
 */
export const inGroup = (p: StoreProduct, id: string) =>
  p.categoryId === id ||
  p.categoryIds.includes(id) ||
  p.collectionIds.includes(id) ||
  p.brandId === id ||
  (id === SALE_GROUP && onSale(p));
export type StoreGroup = Storefront['collections'][number];
export const findGroup = (store: Storefront, id: string): StoreGroup | undefined =>
  id === SALE_GROUP
    ? { id: SALE_GROUP, nameAr: 'تخفيضات', nameEn: 'Sale', imageUrl: null, handle: null }
    : (store.categories.find((c) => c.id === id) ??
      store.collections.find((c) => c.id === id) ??
      store.brands.find((c) => c.id === id));

/** The groups of one kind (categories, collections or brands). */
export const groupsOf = (store: Storefront, kind: StoreGroupKind): StoreGroup[] =>
  ({ category: store.categories, collection: store.collections, brand: store.brands })[kind];

/**
 * Where a group's products are listed, after the store's address (migration 0107): its own page
 * (`/categories/coffee`, `/collections/new-in`, `/brands/roastery`); the sale page and a group
 * the store does not show by `/products?category={id}`. Links are built with `storePath`.
 */
export function groupPath(store: Storefront | null | undefined, id: string): string {
  if (store && id !== SALE_GROUP)
    for (const kind of ['category', 'collection', 'brand'] as const) {
      const group = groupsOf(store, kind).find((g) => g.id === id);
      if (group) return storeGroupPath(kind, group);
    }
  return `/products?category=${encodeURIComponent(id)}`;
}

/** A product's page after the store's address: `/products/{handle}` (migration 0107). */
export const productPath = (p: { productId: string; handle?: string | null }) =>
  storeProductPath(p);

/**
 * Store paths of records known only by id (a design link, a cart line): the product's or the
 * group's readable address once the store's data is in, else the id's (moved by the page).
 */
export function useStorePaths() {
  const { data: store } = useStorefront();
  const products = useStoreProducts();
  const list = products.data?.products;
  return useMemo(
    () => ({
      product: (id: string) =>
        productPath(list?.find((p) => p.productId === id) ?? { productId: id }),
      group: (id: string) => groupPath(store, id),
    }),
    [store, list],
  );
}
export type StorePaths = ReturnType<typeof useStorePaths>;

/**
 * The group a page lists (migration 0107): `/categories/{handle}` and the other group pages
 * give it to the catalog section; null on `/products` (the `?category=` filter applies).
 */
export const RouteGroupContext = createContext<string | null>(null);
/**
 * The groups the store's menus and category sections show: its categories, or its collections
 * while no category is shown (a store whose products only sat in ADR-0121 store categories
 * keeps its navigation until they are linked to the shared categories).
 */
export const navGroups = (store: Storefront | null | undefined): StoreGroup[] =>
  !store ? [] : store.categories.length > 0 ? store.categories : store.collections;

/**
 * The shopper's favourites (ADR-0146), kept in this browser per store. Storage may be missing
 * (private window): the list then lives only while the page is open.
 */
const WISHLIST_EVENT = 'arabat:wishlist';
export function useWishlist() {
  const slug = useSlug();
  const storageKey = `arabat.wishlist.${slug}`;
  const read = (): string[] => {
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) ?? '[]') as unknown;
      return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  };
  const [ids, setIds] = useState<string[]>(read);
  useEffect(() => {
    const sync = () => setIds(read());
    window.addEventListener(WISHLIST_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(WISHLIST_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);
  const toggle = (id: string) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(-200);
    setIds(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* storage unavailable: kept for this page only */
    }
    window.dispatchEvent(new Event(WISHLIST_EVENT));
  };
  return { ids, has: (id: string) => ids.includes(id), toggle };
}

/**
 * The variant a "+" button may add at once: the product's only variant. A product with several
 * sizes or any option group (ADR-0160) has none: its choices make separate cart lines, which a
 * single − n + counter cannot show, so the quick "+" is hidden (owner, 2026-11-03) and the shopper
 * chooses on the product page.
 */
export function quickVariant(p: StoreProduct) {
  if (p.variants.length !== 1) return null;
  if (p.optionGroups.length > 0) return null;
  return p.variants[0]!;
}
